import { useRef, useState, useCallback, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { useAppStore } from '../../store';

// ─── FaceLogin — Reconnaissance faciale pour Helios ─────────────────────────
// Scan automatique, design amber/orange Helios, fallback password

type ScanStatus = 'idle' | 'scanning' | 'processing' | 'success' | 'error' | 'not_recognized';

interface FaceLoginProps {
  onSuccess: () => void;
  onFallback: () => void;
}

export default function FaceLogin({ onSuccess, onFallback }: FaceLoginProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [status, setStatus] = useState<ScanStatus>('idle');
  const [message, setMessage] = useState('');
  const [pulse, setPulse] = useState(false);
  const authLogin = useAppStore((s) => s.authLogin);

  // Camera
  const startCamera = useCallback(async () => {
    // Vérifier que l'API MediaDevices est disponible (requiert HTTPS)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setStatus('error');
      setMessage('Camera non supportee (HTTPS requis)');
      return;
    }

    try {
      // Demander la permission caméra
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err: any) {
      setStatus('error');
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setMessage('Autorisez l\'acces camera dans votre navigateur');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setMessage('Aucune camera detectee');
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        setMessage('Camera utilisee par une autre application');
      } else if (name === 'OverconstrainedError') {
        // Réessayer sans contraintes
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
            setCameraReady(true);
          }
          return;
        } catch {
          setMessage('Camera incompatible');
        }
      } else {
        setMessage('Camera indisponible');
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraReady(false);
  }, []);

  // Capture + envoi
  const captureAndVerify = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || status === 'processing' || status === 'success') return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx || video.videoWidth === 0 || video.readyState < 2) return;

    // Crop 65% central
    const vw = video.videoWidth, vh = video.videoHeight;
    const cw = Math.floor(vw * 0.65), ch = Math.floor(vh * 0.65);
    const cx = Math.floor((vw - cw) / 2), cy = Math.floor((vh - ch) / 2);
    canvas.width = cw; canvas.height = ch;
    ctx.drawImage(video, cx, cy, cw, ch, 0, 0, cw, ch);

    // Anti image noire
    const sample = ctx.getImageData(cw / 4, ch / 4, cw / 2, ch / 2);
    let nonBlack = 0;
    for (let i = 0; i < sample.data.length; i += 16) {
      if (sample.data[i] > 10 || sample.data[i+1] > 10 || sample.data[i+2] > 10) nonBlack++;
    }
    if (nonBlack < (sample.data.length / 16) * 0.05) return;

    const blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', 0.95));
    if (!blob || blob.size < 5000) return;

    setPulse(true);
    setTimeout(() => setPulse(false), 500);
    setStatus('processing');

    try {
      const form = new FormData();
      form.append('image', blob, 'face.jpg');

      const res = await apiClient.post('/auth/face-login', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.token && res.data.user) {
        localStorage.setItem('helios_token', res.data.token);
        authLogin(res.data.token, res.data.user);
        setStatus('success');
        stopCamera();
        setTimeout(onSuccess, 600);
        return;
      }
    } catch (err: any) {
      attemptsRef.current++;
      const detail = err?.response?.data?.detail || '';

      if (detail.includes('non reconnu') || detail.includes('confiance')) {
        setStatus('not_recognized');
        setMessage('Visage non reconnu');
        if (attemptsRef.current >= 3) {
          setTimeout(() => { stopCamera(); onFallback(); }, 1500);
        } else {
          setTimeout(() => setStatus('idle'), 2000);
        }
        return;
      }

      setStatus('error');
      setMessage(detail || 'Erreur');
      setTimeout(() => setStatus('idle'), 2000);
      return;
    }

    setStatus('error');
    setMessage('Reponse invalide');
    setTimeout(() => setStatus('idle'), 2000);
  }, [status, authLogin, onSuccess, onFallback, stopCamera]);

  // Auto-start + scan periodique
  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  useEffect(() => {
    if (cameraReady && (status === 'idle' || status === 'scanning') && !intervalRef.current) {
      const t = setTimeout(() => {
        captureAndVerify();
        intervalRef.current = setInterval(captureAndVerify, 3000);
      }, 1500);
      return () => clearTimeout(t);
    }
    return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  }, [cameraReady, status, captureAndVerify]);

  const ringColor = {
    idle: 'border-amber-500/40',
    scanning: 'border-amber-500/50',
    processing: 'border-yellow-400/50',
    success: 'border-green-400',
    error: 'border-red-400/50',
    not_recognized: 'border-orange-400/50',
  }[status];

  const labelColor = {
    idle: 'text-amber-400',
    scanning: 'text-amber-400',
    processing: 'text-yellow-400',
    success: 'text-green-400',
    error: 'text-red-400',
    not_recognized: 'text-orange-400',
  }[status];

  const label = {
    idle: 'Positionnez votre visage',
    scanning: 'Scan...',
    processing: 'Verification...',
    success: 'Identifie !',
    error: message || 'Erreur',
    not_recognized: 'Non reconnu',
  }[status];

  return (
    <div className="flex flex-col items-center">
      {/* Scanner */}
      <div className={`relative w-44 h-44 rounded-full overflow-hidden border-[3px] ${ringColor} transition-all duration-500 ${pulse ? 'scale-105' : ''}`}>
        <video ref={videoRef} autoPlay playsInline muted
          className="w-full h-full object-cover scale-x-[-1]"
          style={{ filter: status === 'success' ? 'brightness(1.1)' : 'brightness(0.85)' }}
        />
        {status === 'processing' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-10 h-10 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
          </div>
        )}
        {status === 'success' && (
          <div className="absolute inset-0 flex items-center justify-center bg-green-500/20">
            <span className="text-green-400 text-3xl">✓</span>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />

      <p className={`mt-4 text-sm font-medium ${labelColor}`}>{label}</p>

      <button
        onClick={() => { stopCamera(); onFallback(); }}
        className="mt-6 text-xs text-gray-500 hover:text-gray-300 transition-colors"
      >
        Utiliser email / mot de passe
      </button>
    </div>
  );
}
